import * as moment from 'moment';
import lodash from 'lodash';

import {
    PDFWriter
} from 'complionUtil/PDFWriter';

import delegationAuditLogTemplates from './delegationAuditLogTemplates';

export const DATE_FORMAT = 'DD MMM YYYY';
export const DATE_TIME_FORMAT = 'hh:mm A, DD MMM YYYY';

export const DEFAULT_STUDY_DETAILS_HEADERS = [['Principal Investigator', 'Protocol/Study Number', 'Sponsor', 'Site Number']];
const DEFAULT_AUDIT_LOG_COLUMN_HEADERS = [['Time', 'User', 'Action', 'Detail']];



/**
 * A collection of a studies DOA data.
 * @typedef {Object} DOA
 * @property {Array<object>} auditLogEntries an array of audit log entries, ordered by most recent.
 * @property {lodash.Dictionary<User>} auditLogUsers all users found in audit log entries, keyed by user id.
 * @property {lodash.Dictionary<any>} studyTasks keyed by task id
 * @property {lodash.Dictionary<any>} studyRoles keyed by role id
 * @property {lodash.Dictionary<any>} studyTestaments keyed by testament id
 * @property {lodash.Dictionary<any>} studyDelegations keyed by delegation id
 * @property {lodash.Dictionary<any>} studySignatures keyed by study signature id
 * @property {Boolean} isCentralized is the site running a centralized DOA
 * @property {lodash.Dictionary<any>} studyBinder 
 */


export default class DelegationAuditLog {
    /**
     * @param {DOA} DOA 
     * @param {String} timezoneOffset The timezone offset associated with the study.
     * @param {PDFWriter} PDFWriter A PDF utility class used to write audit log data to a PDF.
     */
    constructor(DOA, timezoneOffset, PDFWriterClass = PDFWriter) {
        this.DOA = DOA;
        this.timezoneOffset = timezoneOffset;
        this.pdfWriter = new PDFWriterClass();
        this.auditLogEntries = DOA.auditLogEntries;
    }

    /**
     * Generates a standalone DOA Audit Log PDF
     */
    generate() {
        this.generateHeader();
        this.generateStudyKey();
        this.generateAuditLogTable();
        this.save();
    }

    /**
     * Appends the DOA Audit Log to an existing PDF
     * @param {PDFWriter} pdfWriter 
     */
    append(pdfWriter) {
        this.pdfWriter = pdfWriter;
        this.generateAuditLogTable();
    }

    /**
     * Generates DOA Audit Log Header with study details.
     * @private
     */
    generateHeader() {
        const studyBinder = this.DOA.studyBinder;
        this.pdfWriter.writeHeader(`${studyBinder.title || ''} Study Audit Log `);
        const columns = DEFAULT_STUDY_DETAILS_HEADERS;
        const studyDetails = [[
            studyBinder.primaryInvestigator || '', 
            studyBinder.trialId || '', 
            studyBinder.sponsorCro || '', 
            studyBinder.departmentSiteDg || ''
        ]];
        this.pdfWriter.writeTable({head: columns, body: studyDetails, showHead: true});
        this.pdfWriter.writeHorizontalLine();
    }

    /**
     * Uses DOA study tasks to generate study key.
     * @private
     */
    generateStudyKey() {
        this.pdfWriter.writeHeader('Delegation Key');
        const columns = ['left', 'mid', 'right'];
        const formattedStudyKey = lodash.chain(this.DOA.studyTasks)
            .map(studyTask => `${studyTask.taskIndex}. ${studyTask.taskName}`)
            .chunk(columns.length)
            .value();

        this.pdfWriter.writeTable({head: [columns], body: formattedStudyKey, showHead: false, fontSize: 10, fillColor: '#FFF'});
    }

    /**
     * Generates DOA Audit Log Table
     * @param {Array<String[]>} columns An array of string column headers nested in an array.
     * @private
     */
    generateAuditLogTable(columns) {
        this.pdfWriter.addPage();
        this.pdfWriter.writeHeader('Audit Log Entries');
        const auditTableColumnHeaders = columns || DEFAULT_AUDIT_LOG_COLUMN_HEADERS;
        const formattedAuditLogData = this.formatAuditLogData();
        this.pdfWriter.writeTable({head: auditTableColumnHeaders, body: formattedAuditLogData, showHead: true, theme: 'grid'});
    }

    /**
     * Tags each page with the page heading, including page count and export date.
     * @param {String} exportDate 
     * @private
     */
    tagPageHeaders(exportDate) {
        this.pdfWriter.writePageHeaders(exportDate);
    }

    /**
     * @returns {String} Current date.
     * @private
     */
    getExportDate() {
        return moment().format(DATE_FORMAT);
    }

    /**
     * @param {String} actionDateTime The date and time the audit event occurred.
     * @returns {String} Formatted action datetime with timezone applied.
     * @private
     */
    getActionTimeWithTimezone(actionDateTime) {
        return moment(actionDateTime).utcOffset(this.timezoneOffset).format(DATE_TIME_FORMAT);
    }

    /**
     * Saves the generated PDF to client with study title as part of the file name.
     * @private
     */
    save() {
        const exportDate = this.getExportDate();
        const studyTitle = this.DOA.studyBinder.title;
        this.tagPageHeaders(exportDate);
        const fileName = `${studyTitle}-audit-log-${exportDate}.pdf`;
        this.pdfWriter.savePDF(fileName);
    }

    /**
     * Formats the audit log entries using the delegationAuditLogTemplates.
     * @returns {Array<String[]>}
     * @private
     */
    formatAuditLogData() {
        return this.auditLogEntries.map((auditEntry, auditIndex) => {
            const studyAuditLogTemplate = delegationAuditLogTemplates[auditEntry.actionType] || delegationAuditLogTemplates.DEFAULT;
            const actionTime = this.getActionTimeWithTimezone(auditEntry.createdAt);
            const actionDetails = studyAuditLogTemplate(auditEntry, this.DOA, auditIndex, this.timezoneOffset);
            return [
                actionTime,
                auditEntry.actionUserDisplayName,
                auditEntry.actionType,
                actionDetails,
            ];
        });
    }
}